// Desenho do card de divulgacao (1080px de largura) no canvas.
//
// Reproduz o formato dos cards que os corretores ja usam: foto no topo, barra
// laranja de 6px e faixa branca com bairro / endereco / valor / specs / obras.
// A geometria veio do gerador em PowerShell (make_cards.ps1), com uma troca:
// a fonte e Ubuntu (a mesma do site e do boletim) em vez de Verdana, porque
// Verdana nao existe em Android/iOS e o card sairia diferente em cada aparelho.

const LARANJA = 'rgb(234, 92, 29)';
const CINZA = 'rgb(110, 110, 110)';
const CINZA_CLARO = 'rgb(160, 160, 160)';
const ESCURO = 'rgb(51, 51, 51)';

const CW = 1080;
const X = 40;
const MAXW = CW - X * 2;
const BARRA = 6;

const fonte = (tamanho, peso = 400) => `${peso} ${tamanho}px Ubuntu, Roboto, Arial, sans-serif`;

export const CDN_FOTOS = process.env.REACT_APP_FILES_URL || 'https://files.visualizarimovel.com.br';

// As fontes precisam estar carregadas ANTES do primeiro fillText, senao o
// canvas desenha com a fallback e o card sai com metricas erradas.
// document.fonts.load quer um shorthand com UMA familia — passar a lista
// inteira faz o Firefox recusar a string.
export async function aguardarFontes() {
  if (!document.fonts) return;
  const pedidos = ['700 46px Ubuntu', '700 54px Ubuntu', '400 30px Ubuntu', '400 28px Ubuntu'];
  try {
    await Promise.all(pedidos.map((p) => document.fonts.load(p)));
    await document.fonts.ready;
  } catch (e) {
    // sem a Ubuntu o card sai na fallback do sistema; melhor isso que travar
  }
}

const moeda = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;

const plural = (n, singular, pluralForma) => `${n} ${n === 1 ? singular : pluralForma}`;

export function specsDoImovel(p) {
  const partes = [];
  if (p.fullLandSize) partes.push(`${p.fullLandSize}m²`);

  const quartos = Number(p.bedrooms) || 0;
  const suites = Number(p.suites) || 0;
  if (quartos) {
    let texto = plural(quartos, 'quarto', 'quartos');
    if (suites === quartos && suites === 1) texto += ' (suíte)';
    else if (suites > 0) texto += ` (${plural(suites, 'suíte', 'suítes')})`;
    partes.push(texto);
  }

  const banheiros = Number(p.bathrooms) || 0;
  if (banheiros) partes.push(plural(banheiros, 'banheiro', 'banheiros'));

  const vagas = Number(p.parkingSpots) || 0;
  partes.push(vagas ? plural(vagas, 'vaga', 'vagas') : 'sem vaga');

  return partes.join(' · ');
}

// REGRA (Fabio, 18/08/2026): o card vai do corretor para o CLIENTE dele, entao
// leva so rua, bairro e cidade — nunca o numero do predio nem a unidade. Quem
// recebe nao precisa do endereco exato para se interessar, e imovel VAZIO com
// endereco exato circulando por WhatsApp e outra coisa.
//
// Vale para os TRES lugares por onde o endereco sai daqui: o card desenhado, o
// texto que acompanha o compartilhamento e o nome do arquivo baixado. Foi por
// isso que o `normalizarComplemento` (que formatava "apto 605") saiu junto:
// codigo morto que so serviria para alguem religar a unidade sem perceber.
export function enderecoDoImovel(p) {
  return p.address || '';
}

// "Botafogo · Rio de Janeiro" — o quanto de localizacao o cliente precisa.
export function localDoImovel(p) {
  const cidade = p.city && p.city.name;
  return [p.neighborhoodName, cidade].filter(Boolean).join(' · ');
}

// Telefone brasileiro em qualquer grafia comum. Rede de seguranca, nao extrator.
const TELEFONE = /\(?\d{2}\)?\s?9?\d{4}[-\s.]?\d{4}/;

// Daqui em diante o texto deixa de ser andamento de obra e vira instrucao de
// visita/chave -- que nao pode ir para um card que o corretor manda ao comprador.
const INICIO_DA_INSTRUCAO =
  /\b(chaves?|visita\w*|contatos?|whatsapp|zap|tels?\.?|telefones?|falar\s+(em\s+nome|com)|apresentar|agendar|portaria|guarita|corretor\w*)\b/i;

// So aparece quando o cadastro tem o andamento da obra.
// Sem invencao: imovel sem o dado sai sem a linha.
//
// A fonte preferida e o `constructionStatus`, que a API passou a devolver ja
// recortado (src/config/publicProperty.js do backend). O caminho pelo
// `sellingNotes` fica como ponte: ele some da resposta publica quando a API
// subir, e ate la precisa do mesmo corte -- no cadastro, andamento e instrucao
// de chave costumam vir na MESMA linha, e o card estava saindo impresso com o
// nome e o telefone de quem guarda a chave.
export function obrasDoImovel(p) {
  if (p.constructionStatus) return `Obras: ${p.constructionStatus}`;

  const m = (p.sellingNotes || '').match(/Obras?\s*:\s*([^\n\r]+)/i);
  if (!m) return null;

  let linha = m[1];
  const corte = linha.search(INICIO_DA_INSTRUCAO);
  if (corte >= 0) linha = linha.slice(0, corte);
  linha = linha.replace(/[\s.,;:\-–—]+$/, '').trim();

  if (!linha) return null;
  if (TELEFONE.test(linha)) return null;
  if (linha.length > 60) return null;

  return `Obras: ${linha}`;
}

export function capaDoImovel(p) {
  const imagens = (p.images || []).filter((i) => i && i.image);
  if (!imagens.length) return null;
  const capa = imagens.slice().sort((a, b) => (a.order || 0) - (b.order || 0))[0];
  return `${CDN_FOTOS}/${p._id}/fotos/${capa.image}`;
}

const carregarFoto = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    // sem isto o canvas fica "tainted" e o toBlob falha; o CDN responde
    // access-control-allow-origin quando a requisicao manda Origin
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('falha ao carregar a foto'));
    img.src = url;
  });

const quebrarLinhas = (ctx, texto, larguraMax) => {
  const linhas = [];
  let atual = '';
  texto.split(' ').forEach((palavra) => {
    const teste = atual ? `${atual} ${palavra}` : palavra;
    if (ctx.measureText(teste).width <= larguraMax) atual = teste;
    else {
      if (atual) linhas.push(atual);
      atual = palavra;
    }
  });
  if (atual) linhas.push(atual);
  return linhas;
};

/**
 * Desenha o card do imovel e devolve um Blob JPEG de 1080px de largura.
 */
export async function gerarCard(p) {
  const urlFoto = capaDoImovel(p);
  if (!urlFoto) throw new Error('imóvel sem foto de capa');

  const foto = await carregarFoto(urlFoto);
  const alturaFoto = Math.round((CW * foto.naturalHeight) / foto.naturalWidth);

  const bairro = p.neighborhoodName || '';
  const cidade = (p.city && p.city.name) || '';
  const endereco = [enderecoDoImovel(p), cidade].filter(Boolean).join(' · ');
  const obras = obrasDoImovel(p);
  const specs = specsDoImovel(p);

  const temDesconto = p.descountPrice && Number(p.descountPrice) < Number(p.sellingPrice);
  const valor = moeda(temDesconto ? p.descountPrice : p.sellingPrice);
  const valorAntigo = temDesconto ? `Era: ${moeda(p.sellingPrice)}` : null;

  // 1a passada: medir para saber a altura da faixa
  const medidor = document.createElement('canvas').getContext('2d');
  medidor.font = fonte(30);
  const linhasEndereco = quebrarLinhas(medidor, endereco, MAXW);

  // specs encolhem ate caber numa linha so
  let tamanhoSpecs = 28;
  while (tamanhoSpecs > 20) {
    medidor.font = fonte(tamanhoSpecs);
    if (medidor.measureText(specs).width <= MAXW) break;
    tamanhoSpecs -= 1;
  }

  const alturaFaixa =
    30 + 56 + linhasEndereco.length * 40 + 10 + 66 + 14 + 40 + (obras ? 8 + 40 : 0) + 30;

  const canvas = document.createElement('canvas');
  canvas.width = CW;
  canvas.height = alturaFoto + BARRA + alturaFaixa;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(foto, 0, 0, CW, alturaFoto);
  ctx.fillStyle = LARANJA;
  ctx.fillRect(0, alturaFoto, CW, BARRA);

  ctx.textBaseline = 'top';
  let y = alturaFoto + BARRA + 30;

  ctx.font = fonte(46, 700);
  ctx.fillStyle = LARANJA;
  ctx.fillText(bairro, X, y);
  y += 56;

  ctx.font = fonte(30);
  ctx.fillStyle = CINZA;
  linhasEndereco.forEach((linha) => {
    ctx.fillText(linha, X, y);
    y += 40;
  });

  y += 10;
  ctx.font = fonte(54, 700);
  ctx.fillStyle = ESCURO;
  ctx.fillText(valor, X, y);
  if (valorAntigo) {
    const larguraValor = ctx.measureText(valor).width;
    ctx.font = fonte(28);
    ctx.fillStyle = CINZA_CLARO;
    ctx.fillText(valorAntigo, X + larguraValor + 24, y + 26);
  }
  y += 66 + 14;

  ctx.font = fonte(tamanhoSpecs);
  ctx.fillStyle = CINZA;
  ctx.fillText(specs, X, y);
  y += 40;

  if (obras) {
    y += 8;
    ctx.font = fonte(28);
    ctx.fillStyle = CINZA;
    ctx.fillText(obras, X, y);
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('falha ao gerar o JPEG'))),
      'image/jpeg',
      0.92
    );
  });
}

// ⚠️ Este site nao pode conter NENHUMA referencia a Imovel Vazio — nem no
// visivel, nem nos links que o corretor repassa ao cliente. Por isso o link do
// imovel aponta para o visualizador deste proprio dominio (/{id}), e nao para
// o site da marca.
export const SITE = 'https://www.visualizarimovel.com.br';

export const linkDoImovel = (p) => `${SITE}/${p._id}`;

// Legenda que acompanha o card no WhatsApp.
export function textoCompartilhar(p) {
  const temDesconto = p.descountPrice && Number(p.descountPrice) < Number(p.sellingPrice);
  const valor = moeda(temDesconto ? p.descountPrice : p.sellingPrice);
  return [
    `${enderecoDoImovel(p)} — ${localDoImovel(p)}`,
    `${valor} · ${specsDoImovel(p)}`,
    linkDoImovel(p),
  ].join('\n');
}

// "Botafogo - Rua Assis Bueno, 46.jpg"
// Sem acento nem caractere especial: o nome viaja por WhatsApp, Android e iOS.
// Sem sufixo de marca, pelo mesmo motivo do link acima.
export function nomeDoArquivo(p) {
  const bruto = `${p.neighborhoodName || ''} - ${p.address || ''}`;
  const semAcento = bruto.normalize('NFD').replace(/[^\x20-\x7E]/g, '');
  const limpo = semAcento
    .replace(/[^\w\s,.-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return `${limpo}.jpg`;
}
